/**
 * Product Hub — catalog vocabulary and the pure logic the catalog resolvers need.
 *
 * Codes are English and labels are Hebrew; see ADR-017 for why this module deviates from the
 * repo's usual "store the Hebrew label" habit.
 *
 * No React, no MUI, no browser globals — the backend imports this file directly through the
 * `@malash-office/types/product-hub/catalog` alias.
 */
import { PhOption, phOptions } from './productHub.core';

/* ------------------------------------------------------------------ */
/* Products                                                            */
/* ------------------------------------------------------------------ */

/**
 * What kind of thing the product is.
 *
 * Deliberately separate from `VARIANT_STRUCTURES`: the spec (§4) is explicit that one `type`
 * field must not carry both meanings. "A bundle" and "has colours" are orthogonal — a bundle can
 * itself come in two sizes.
 */
export const PRODUCT_TYPES = ['physical', 'digital', 'bundle'] as const;
export type ProductType = (typeof PRODUCT_TYPES)[number];

export const PRODUCT_TYPE_LABELS: Record<ProductType, string> = {
  physical: 'מוצר פיזי',
  digital: 'מוצר דיגיטלי',
  bundle: 'מארז'
};

export const PRODUCT_TYPE_OPTIONS: PhOption<ProductType>[] = phOptions(PRODUCT_TYPES, PRODUCT_TYPE_LABELS);

/** Whether the product sells as one unit or as a matrix of variants. */
export const VARIANT_STRUCTURES = ['simple', 'variable'] as const;
export type VariantStructure = (typeof VARIANT_STRUCTURES)[number];

export const VARIANT_STRUCTURE_LABELS: Record<VariantStructure, string> = {
  simple: 'מוצר פשוט',
  variable: 'מוצר עם וריאציות'
};

export const VARIANT_STRUCTURE_OPTIONS: PhOption<VariantStructure>[] = phOptions(
  VARIANT_STRUCTURES,
  VARIANT_STRUCTURE_LABELS
);

/**
 * A product's lifecycle. `draft` exists so the three-step wizard can save and come back — which
 * is also why the taxonomy foreign keys are nullable in the database and only required on the
 * transition to `active`.
 */
export const PRODUCT_STATUSES = ['draft', 'active', 'inactive'] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

export const PRODUCT_STATUS_LABELS: Record<ProductStatus, string> = {
  draft: 'טיוטה',
  active: 'פעיל',
  inactive: 'לא פעיל'
};

export const PRODUCT_STATUS_OPTIONS: PhOption<ProductStatus>[] = phOptions(PRODUCT_STATUSES, PRODUCT_STATUS_LABELS);

/** How a bundle's availability is derived. Only meaningful when `productType === 'bundle'`. */
export const BUNDLE_INVENTORY_MODES = ['components', 'independent'] as const;
export type BundleInventoryMode = (typeof BUNDLE_INVENTORY_MODES)[number];

export const BUNDLE_INVENTORY_MODE_LABELS: Record<BundleInventoryMode, string> = {
  components: 'מלאי לפי רכיבי המארז',
  independent: 'מלאי עצמאי'
};

export const BUNDLE_INVENTORY_MODE_OPTIONS: PhOption<BundleInventoryMode>[] = phOptions(
  BUNDLE_INVENTORY_MODES,
  BUNDLE_INVENTORY_MODE_LABELS
);

/* ------------------------------------------------------------------ */
/* simple → variable conversion                                        */
/**
 * Does the variant name say anything the product name did not?
 *
 * Every screen that lists variants puts the product name on the first line and the variant name
 * under it — the second line is what tells two rows of the same product apart. A simple product
 * has one hidden default variant (ADR-001) that the wizard names after the product, so on those
 * rows the second line is the same words twice: noise, and on a wide matrix it costs a line of
 * height per row for nothing.
 *
 * Compared by name rather than by `isDefault`, deliberately. A variable product can still carry a
 * leftover default variant named after the product — the pricelist matrix shows exactly that beside
 * its real לבן / שחור / כתום siblings — and that row is just as redundant as a simple product's.
 * The question the caller is asking is "do these two lines differ", not "which variant is this".
 */
export const isVariantNameRedundant = (productName: string | null, variantName: string | null): boolean => {
  const variant = variantName?.trim();
  if (!variant) return true;
  return variant === productName?.trim();
};

/* ------------------------------------------------------------------ */

export interface ConversionCheck {
  ok: boolean;
  /** Hebrew explanation when `ok` is false, so the API can answer without composing copy. */
  reason: string | null;
}

/**
 * May this product be converted from simple to variable?
 *
 * The conversion itself is a dedicated command, not a dropdown change (ADR-012): the existing
 * default variant survives and becomes variant #1, keeping its SKU, barcode, inventory, prices,
 * store links and media. Everything this guard rejects is a case where that promise cannot be
 * kept, so it is checked *before* the transaction opens rather than discovered inside it.
 */
export const canConvertToVariable = (
  product: { variantStructure: string; productType: string },
  variants: { id: number; isDefault: boolean; archivedAt?: Date | string | null }[],
  attributeIds: number[]
): ConversionCheck => {
  if (product.variantStructure === 'variable') {
    return { ok: false, reason: 'המוצר כבר מוגדר כמוצר עם וריאציות' };
  }
  if (product.productType === 'bundle') {
    return { ok: false, reason: 'מארז אינו יכול לקבל וריאציות בשלב זה' };
  }

  const live = variants.filter(v => !v.archivedAt);
  if (live.length !== 1) {
    // Either the default variant is missing (ADR-001 broken) or extra variants already exist —
    // in both cases "the existing variant becomes variant #1" is not a well-defined instruction.
    return { ok: false, reason: 'המרה אפשרית רק למוצר שיש לו וריאציית ברירת מחדל אחת בלבד' };
  }
  if (!live[0].isDefault) {
    return { ok: false, reason: 'לא נמצאה וריאציית ברירת מחדל למוצר' };
  }
  if (!attributeIds.length) {
    return { ok: false, reason: 'יש לבחור לפחות מאפיין אחד ליצירת הוריאציות' };
  }

  return { ok: true, reason: null };
};

/* ------------------------------------------------------------------ */
/* Attributes                                                          */
/* ------------------------------------------------------------------ */

/**
 * What kind of value an attribute carries.
 *
 * Only `option` can drive a variant matrix in the MVP (spec §9). `text`/`number`/`boolean`
 * attributes are specification data hanging off a product or variant — they describe it, they do
 * not multiply it. Letting a free-text attribute generate variants would produce a combinatorial
 * explosion out of a field somebody typed into.
 */
export const ATTRIBUTE_DATA_TYPES = ['option', 'text', 'number', 'boolean'] as const;
export type AttributeDataType = (typeof ATTRIBUTE_DATA_TYPES)[number];

export const ATTRIBUTE_DATA_TYPE_LABELS: Record<AttributeDataType, string> = {
  option: 'רשימת ערכים',
  text: 'טקסט חופשי',
  number: 'מספר',
  boolean: 'כן/לא'
};

export const ATTRIBUTE_DATA_TYPE_OPTIONS: PhOption<AttributeDataType>[] = phOptions(
  ATTRIBUTE_DATA_TYPES,
  ATTRIBUTE_DATA_TYPE_LABELS
);

/** Only `option` attributes may be used to build a variant matrix. */
export const canDriveVariants = (dataType: string): boolean => dataType === 'option';

/**
 * Which column of `VariantAttributeValues` this data type is allowed to populate.
 *
 * The table has four value columns and exactly one of them may hold a value per row. The spec
 * (§9) makes enforcing that a Service Layer duty rather than a DB constraint, because the rule
 * depends on `Attributes.dataType` — a different table. This function is that rule, in one place,
 * so the write path and any future importer cannot drift apart.
 */
export const attributeValueColumnFor = (
  dataType: string
): 'optionId' | 'textValue' | 'numberValue' | 'booleanValue' | null => {
  switch (dataType) {
    case 'option':
      return 'optionId';
    case 'text':
      return 'textValue';
    case 'number':
      return 'numberValue';
    case 'boolean':
      return 'booleanValue';
    default:
      return null;
  }
};

/* ------------------------------------------------------------------ */
/* Categories                                                          */
/* ------------------------------------------------------------------ */

/** The shape the tree helpers need. Any row with these fields works; extras are carried through. */
export interface CategoryLike {
  id: number;
  parentCategoryId: number | null;
  name: string;
  sortOrder?: number;
}

export type CategoryTreeNode<T extends CategoryLike> = T & { children: CategoryTreeNode<T>[] };

/**
 * Turn flat category rows into a nested tree.
 *
 * Rows whose parent is missing from the input — archived, filtered out, or simply not fetched —
 * are surfaced as roots rather than silently dropped. A category that vanishes from the picker
 * because its parent was archived is a support ticket nobody can diagnose.
 *
 * Siblings are ordered by `sortOrder` then `name`, so the UI never has to sort again and two
 * screens never disagree about the order.
 */
export const buildCategoryTree = <T extends CategoryLike>(rows: T[]): CategoryTreeNode<T>[] => {
  const byId = new Map<number, CategoryTreeNode<T>>(rows.map(row => [row.id, { ...row, children: [] }]));
  const roots: CategoryTreeNode<T>[] = [];

  byId.forEach(node => {
    const parent = node.parentCategoryId === null ? undefined : byId.get(node.parentCategoryId);
    if (parent) parent.children.push(node);
    else roots.push(node);
  });

  const sortNodes = (nodes: CategoryTreeNode<T>[]) => {
    nodes.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name, 'he'));
    nodes.forEach(node => sortNodes(node.children));
  };
  sortNodes(roots);

  return roots;
};

/**
 * The chain from a category up to its root, nearest ancestor first.
 *
 * The shipping-rule resolver needs this so a rule written on a parent category also matches its
 * descendants (ADR-009). It is cycle-safe: a corrupt parent chain stops instead of hanging.
 */
export const categoryAncestors = <T extends CategoryLike>(rows: T[], categoryId: number): T[] => {
  const byId = new Map(rows.map(row => [row.id, row]));
  const chain: T[] = [];
  const seen = new Set<number>();

  let current = byId.get(categoryId)?.parentCategoryId ?? null;
  while (current !== null && !seen.has(current)) {
    seen.add(current);
    const parent = byId.get(current);
    if (!parent) break;
    chain.push(parent);
    current = parent.parentCategoryId;
  }

  return chain;
};

/**
 * A category together with every category beneath it, ids only.
 *
 * `categoryAncestors`' mirror image, and the filter counterpart to it: a product carries one
 * `categoryId` — the deepest node — so filtering a list by "ריהוט" has to match everything filed
 * under "ריהוט / כיסאות" too, or the parent category selects almost nothing and reads as broken.
 *
 * Cycle-safe like its siblings: a corrupt parent chain stops instead of hanging. The result always
 * contains `categoryId` itself, even when it is absent from `rows` — the caller asked about it.
 */
export const categoryDescendantIds = <T extends CategoryLike>(rows: T[], categoryId: number): number[] => {
  const childrenOf = new Map<number, number[]>();
  rows.forEach(row => {
    if (row.parentCategoryId === null) return;
    const siblings = childrenOf.get(row.parentCategoryId) ?? [];
    siblings.push(row.id);
    childrenOf.set(row.parentCategoryId, siblings);
  });

  const collected = new Set<number>([categoryId]);
  const queue = [categoryId];

  while (queue.length) {
    const current = queue.shift() as number;
    for (const child of childrenOf.get(current) ?? []) {
      if (collected.has(child)) continue;
      collected.add(child);
      queue.push(child);
    }
  }

  return [...collected];
};

/**
 * Would re-parenting `categoryId` under `newParentId` create a cycle?
 *
 * A category cannot be its own ancestor. Without this check the tree builder produces an orphaned
 * island (the nodes reference each other, so neither is ever reached from a root) and the rows
 * simply disappear from every screen — a silent data loss, not an error.
 *
 * Returns true for the self-parent case too, which is the same mistake one level shorter.
 */
export const wouldCreateCycle = <T extends CategoryLike>(
  rows: T[],
  categoryId: number,
  newParentId: number | null
): boolean => {
  if (newParentId === null) return false;
  if (newParentId === categoryId) return true;

  const byId = new Map(rows.map(row => [row.id, row]));
  const seen = new Set<number>();

  let current: number | null = newParentId;
  while (current !== null && !seen.has(current)) {
    if (current === categoryId) return true;
    seen.add(current);
    current = byId.get(current)?.parentCategoryId ?? null;
  }

  return false;
};
